import { CopytigerPyLambdaFetch, CopytigerLambdaFetch } from "../utils/fetch-utils";

export interface StyleGuideOption {
  id: string;
  name: string;
  s3uri: string;
  lastmodified: string;
  lastuser: string;
  company: string;
  description?: string;
}

export interface ModelInstanceOption {
  id: string;
  name: string;
  s3Uri: string;
  lastModified: string;
  company: string;
  description?: string;
}

export const getCopytigerStyleGuides = async (company: string): Promise<StyleGuideOption[]> => {
  try {
    const response = await CopytigerLambdaFetch("/getCopytigerStyleGuides", {
      method: "GET",
      params: {
        company,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch style guides: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data || [];
  } catch (error) {
    console.error(`Unable to fetch style guides for ${company}:`, error);
    return [];
  }
};

export const addCopytigerStyleGuide = async (
  company: string,
  name: string,
  styleGuideText: string,
  description?: string,
  id?: string
): Promise<StyleGuideOption> => {
  try {
    const response = await CopytigerLambdaFetch("/addCopytigerStyleGuide", {
      method: "POST",
      body: JSON.stringify({
        company,
        name,
        styleGuideText,
        description,
        ...(id && { id }),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add style guide: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data;
  } catch (error) {
    console.error(`Unable to add style guide for ${company}:`, error);
    throw error;
  }
};

export const deleteCopytigerStyleGuide = async (
  company: string,
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await CopytigerLambdaFetch("/deleteCopytigerStyleGuide", {
      method: "DELETE",
      params: {
        company,
        id,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete style guide: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data;
  } catch (error) {
    console.error(`Unable to delete style guide ${id} for ${company}:`, error);
    throw error;
  }
};

export const generateCopytigerStyleGuide = async (company: string): Promise<string> => {
  try {
    const response = await CopytigerPyLambdaFetch("/getCopytigerStyleGuides", {
      method: "GET",
      params: {
        companyID: company,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to generate style guide: ${response.status}`);
    }

    const result = await response.json();
    const generatedStyleGuide = result.body ? JSON.parse(result.body) : result;

    return typeof generatedStyleGuide === "string"
      ? generatedStyleGuide
      : JSON.stringify(generatedStyleGuide, null, 2);
  } catch (error) {
    console.error(`Unable to generate style guide for ${company}:`, error);
    throw error;
  }
};

export const getCopytigerModelInstances = async (
  company: string
): Promise<ModelInstanceOption[]> => {
  try {
    const response = await CopytigerLambdaFetch("/getCopytigerInstances", {
      method: "GET",
      params: {
        company,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch model instances: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data || [];
  } catch (error) {
    console.error(`Unable to fetch model instances for ${company}:`, error);
    return [];
  }
};

export const addCopytigerModelInstance = async (
  company: string,
  name: string,
  vectorstoreData: string | object,
  description?: string,
  id?: string
): Promise<ModelInstanceOption> => {
  try {
    const response = await CopytigerLambdaFetch("/addCopytigerInstance", {
      method: "POST",
      body: JSON.stringify({
        company,
        name,
        vectorstoreData,
        description,
        ...(id && { id }),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add model instance: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data;
  } catch (error) {
    console.error(`Unable to add model instance for ${company}:`, error);
    throw error;
  }
};

export const deleteCopytigerModelInstance = async (
  company: string,
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await CopytigerLambdaFetch("/deleteCopytigerInstance", {
      method: "DELETE",
      params: {
        company,
        id,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete model instance: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data;
  } catch (error) {
    console.error(`Unable to delete model instance ${id} for ${company}:`, error);
    throw error;
  }
};

export const generateCopytigerAds = async (
  companyID: string,
  keywords: string[],
  styleGuideUuid: string,
  instanceUuid: string,
  temperature: number
): Promise<any> => {
  try {
    const payload = {
      companyID,
      keywords,
      style_guide_uuid: styleGuideUuid,
      instance_uuid: instanceUuid,
      temperature,
    };


    const response = await CopytigerPyLambdaFetch("/generateAds", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate ads: ${response.status}`);
    }

    const data = await response.json();
    return data.body || data;
  } catch (error) {
    console.error(`Unable to generate ads for ${companyID}:`, error);
    throw error;
  }
};
